import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";

import { getEntitlements, getPlans, upgradePlan } from "@/api/client";
import { PlanView } from "@/api/types";
import { PageTitle } from "@/components/PageTitle";
import { BuilderReturnCrumb } from "../booking/BuilderReturnCrumb";
import { useProfile } from "./ProfileContext";
import { cn } from "@/lib/utils";

/**
 * Quota rows, in display order, mirroring the public pricing table's "Booking links" group.
 *
 * One row per offering, because that is how the caps are enforced and how they are sold. There used
 * to be a single "live booking links" line, which was really the ad-hoc count under a name implying
 * all three, and a "bookings / month" line that nothing enforced at all. A page whose buttons change
 * your plan is the last place that should overstate what the plan does.
 *
 * A value of -1 means unlimited.
 */
const QUOTAS: { key: string; label: string; value: (p: PlanView) => number }[] = [
  { key: "availability_patterns", label: "availability patterns", value: (p) => p.availability_patterns },
  { key: "adhoc_live", label: "single-use links at a time", value: (p) => p.adhoc_live },
  { key: "adhoc_per_month", label: "single-use links a month", value: (p) => p.adhoc_per_month },
  { key: "rolling_live", label: "rolling links at a time", value: (p) => p.rolling_live },
  { key: "polls_live", label: "polls at a time", value: (p) => p.polls_live },
  { key: "polls_per_month", label: "polls a month", value: (p) => p.polls_per_month },
  { key: "link_days", label: "days a link stays open", value: (p) => p.link_days },
];

/**
 * Boolean features, in display order, each driven by the capability that actually gates it rather
 * than by a hardcoded tier list. That list had drifted: it said AI was Pro by comparing tier names,
 * so a tier change would have had to be remembered here as well as in the backend.
 *
 * `varies` marks Enterprise features whose price is quoted, not fixed (rendered with a *). Those two
 * are still tier comparisons because neither has a capability yet; neither is enforced either, and
 * both are quote-only, so they read as "talk to us" rather than as an entitlement.
 */
const FEATURES: { label: string; has: (p: PlanView) => boolean; varies?: boolean }[] = [
  { label: "Agentic scheduling: your agent negotiates a time by chat", has: (p) => p.ai_scheduling },
  { label: "AI-drafted booking descriptions", has: (p) => p.ai_scheduling },
  { label: "Personalised booking link", has: (p) => p.custom_branding },
  { label: "Audit log: the full history of every link", has: (p) => p.audit_log },
  { label: "Organisation policy · admin portal", has: (p) => p.tier === "ENTERPRISE", varies: true },
  { label: "SSO", has: (p) => p.tier === "ENTERPRISE", varies: true },
];

/** Higher-is-better with -1 treated as unlimited (∞). */
const isHigher = (a: number, b: number) => (a < 0 ? Infinity : a) > (b < 0 ? Infinity : b);

/**
 * Self-serve plan chooser. Payment lands later; for now "upgrade" just switches
 * the caller's tier so the entitlement changes are usable end to end. Renders
 * the plan catalogue from GET /entitlements/plans and highlights the current one.
 */
/**
 * Tier identity colours, deliberately left on the raw palette by the token migration.
 *
 * BASIC already uses --brand-blue, so the obvious move was to finish the job and put
 * the other three on brand tokens too. Measured against white, that makes them worse:
 * indigo-500 reads 4.47 and violet-500 4.23 today, --brand-teal would read 2.11 and
 * --brand-coral 3.31. Only --brand-blue improves on it, at 4.92, and that is BASIC's.
 *
 * So a tier's colour is not a token decision, it is a brand one: the palette has four
 * hues and there are four tiers, one of which is spoken for. Worth settling properly
 * rather than as a side effect of a codemod. Note the current pair is already under
 * AA for the label that sits on it.
 */
const TIER_META: Record<string, { bar: string; ring: string; blurb: string }> = {
  FREE: { bar: "from-slate-300 to-slate-400", ring: "ring-slate-300", blurb: "Get started" },
  BASIC: { bar: "from-brand-blue/70 to-brand-blue", ring: "ring-brand-blue", blurb: "For regulars" },
  PRO: { bar: "from-indigo-400 to-violet-500", ring: "ring-violet-400", blurb: "For power users" },
  ENTERPRISE: { bar: "from-amber-300 to-orange-400", ring: "ring-amber-400", blurb: "For teams" },
};
const ORDER = ["FREE", "BASIC", "PRO", "ENTERPRISE"];

export function UpgradePage() {
  const navigate = useNavigate();
  // A tier in beta testing is admin-only. The server decides both halves: `plan.beta` says the tier
  // is restricted, `profile.admin` says who may still switch onto it, and POST /entitlements/upgrade
  // refuses anyone else with a 403. This page holds no tier list of its own.
  const profile = useProfile().state;
  const isAdmin = profile.status === "ready" && profile.profile.admin;
  const [plans, setPlans] = React.useState<PlanView[] | null>(null);
  const [current, setCurrent] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [done, setDone] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    Promise.all([getPlans(), getEntitlements()])
      .then(([p, e]) => {
        setPlans(p.slice().sort((a, b) => ORDER.indexOf(a.tier) - ORDER.indexOf(b.tier)));
        setCurrent(e.tier);
      })
      .catch(() => setError("Couldn't load the plans. Try again."));
  }, []);

  async function choose(tier: string) {
    setError(null);
    setBusy(tier);
    try {
      const updated = await upgradePlan(tier);
      setCurrent(updated.tier);
      setDone(tier);
    } catch {
      setError("Couldn't switch your plan. Try again.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="mx-auto max-w-6xl space-y-6 px-4 py-8 font-comic-body">
      <BuilderReturnCrumb />
      <header className="text-center">
        <Link
          to="/profile"
          className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:underline"
        >
          ← Back to profile
        </Link>
        <PageTitle className="mt-2">Choose your plan</PageTitle>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          Upgrade any time. No card needed for now. Pick a plan and it applies straight away.
        </p>
      </header>

      {error && (
        <p className="mx-auto max-w-md rounded-xl border border-border bg-destructive/10 px-4 py-2 text-center text-sm text-destructive-strong">
          {error}
        </p>
      )}

      {plans && (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan, index) => {
            const meta = TIER_META[plan.tier] ?? TIER_META.FREE;
            const isCurrent = current === plan.tier;
            const locked = plan.beta && !isAdmin;
            // A card nobody here can choose doesn't get the "Popular" pill or the CTA gradient:
            // both sell an action that isn't on offer, and the pill would collide with the beta
            // badge in the same corner.
            const recommended = plan.tier === "PRO" && !locked;
            const prev = index > 0 ? plans[index - 1] : null;
            return (
              <div
                key={plan.tier}
                className={cn(
                  "relative flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition",
                  isCurrent ? cn("ring-2", meta.ring) : "border-border hover:shadow-md",
                )}
              >
                {plan.beta ? (
                  <span className="absolute right-3 top-3 rounded-full border border-brand-contrast/40 bg-background px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-contrast">
                    Beta
                  </span>
                ) : (
                  recommended &&
                  !isCurrent && (
                    <span className="absolute right-3 top-3 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#fff]">
                      Popular
                    </span>
                  )
                )}
                <div className={cn("h-1.5 bg-gradient-to-r", meta.bar)} />
                <div className="flex flex-1 flex-col p-5">
                  <div className="font-display text-xl font-bold tracking-tight">{titleCase(plan.tier)}</div>
                  <div className="text-xs text-muted-foreground">{meta.blurb}</div>

                  <PlanFeatures plan={plan} prev={prev} />

                  <div className="mt-5 pt-1">
                    {isCurrent ? (
                      <div className="rounded-xl border border-border bg-muted/40 py-2 text-center font-display text-sm font-semibold text-muted-foreground">
                        {done === plan.tier ? "You're on this plan ✓" : "Your plan"}
                      </div>
                    ) : locked ? (
                      // An inert block, not a disabled button: a button that never fires is still a
                      // button, and this one is not waiting on anything the user can do.
                      <>
                        <div className="rounded-xl border border-brand-contrast/30 bg-muted/40 py-2 text-center font-display text-sm font-semibold text-muted-foreground">
                          Beta testing
                        </div>
                        <p className="mt-1.5 text-center text-xs text-muted-foreground">
                          Not generally available
                        </p>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={busy != null}
                        onClick={() => choose(plan.tier)}
                        className={cn(
                          "w-full rounded-xl px-4 py-2 font-display text-sm font-semibold shadow-sm transition disabled:opacity-50",
                          recommended
                            // Literal ink on a literal gradient; --brand-blue-foreground would
                            // flip to near-black in dark and vanish into the violet.
                            ? "bg-gradient-to-r from-indigo-500 to-violet-500 text-[#fff] hover:brightness-110"
                            : "bg-brand-blue text-brand-blue-foreground hover:brightness-110",
                        )}
                      >
                        {busy === plan.tier ? "Switching…" : `Switch to ${titleCase(plan.tier)}`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {plans && (
        <p className="text-center text-xs text-muted-foreground">
          <span className="font-semibold text-warning-strong">*</span> Enterprise features are tailored.
          Pricing varies.{" "}
          <a href="mailto:hello@delgentic.com" className="underline hover:text-foreground">
            Talk to us
          </a>
          .
        </p>
      )}

      {done && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="flex items-center gap-1.5 rounded-full bg-brand-blue px-5 py-2 font-display text-sm font-semibold text-brand-blue-foreground shadow-sm transition hover:brightness-110"
          >
            <Sparkles className="h-4 w-4" /> Done, back to profile
          </button>
        </div>
      )}
    </main>
  );
}

/**
 * A plan's feature list, read cumulatively. The base tier lists everything it includes;
 * each higher tier shows "Everything in <prev>, plus:" and only the quota bumps + features
 * it newly gains — so there's no separate "add-ons" section to cross-reference.
 */
function PlanFeatures({ plan, prev }: { plan: PlanView; prev: PlanView | null }) {
  if (!prev) {
    return (
      <ul className="mt-4 space-y-2 text-sm">
        {QUOTAS.map((q) => (
          <QuotaLine key={q.key} label={q.label} value={q.value(plan)} />
        ))}
        {FEATURES.filter((f) => f.has(plan)).map((f) => (
          <FeatureLine key={f.label} label={f.label} varies={f.varies} />
        ))}
      </ul>
    );
  }
  const quotaBumps = QUOTAS.filter((q) => isHigher(q.value(plan), q.value(prev)));
  const newFeatures = FEATURES.filter((f) => f.has(plan) && !f.has(prev));
  return (
    <ul className="mt-4 space-y-2 text-sm">
      <li className="font-display text-sm font-semibold">
        Everything in {titleCase(prev.tier)}, plus:
      </li>
      {quotaBumps.map((q) => (
        <QuotaLine key={q.key} label={q.label} value={q.value(plan)} indent />
      ))}
      {newFeatures.map((f) => (
        <FeatureLine key={f.label} label={f.label} varies={f.varies} indent />
      ))}
      {quotaBumps.length === 0 && newFeatures.length === 0 && (
        <li className="pl-3 text-muted-foreground">Same allowances as {titleCase(prev.tier)}</li>
      )}
    </ul>
  );
}

function QuotaLine({ label, value, indent }: { label: string; value: number; indent?: boolean }) {
  return (
    <li className={cn("flex items-center gap-2", indent && "pl-3")}>
      <Check className="h-4 w-4 shrink-0 text-brand-blue" />
      <span>
        <span className="font-display font-semibold tabular-nums">
          {value < 0 ? "Unlimited" : value}
        </span>{" "}
        {label}
      </span>
    </li>
  );
}

function FeatureLine({
  label,
  indent,
  varies,
}: {
  label: string;
  indent?: boolean;
  varies?: boolean;
}) {
  return (
    <li className={cn("flex items-center gap-2", indent && "pl-3")}>
      <Sparkles className="h-4 w-4 shrink-0 text-violet-500" />
      <span>
        {label}
        {varies && <sup className="ml-0.5 font-semibold text-warning-strong">*</sup>}
      </span>
    </li>
  );
}

function titleCase(tier: string): string {
  return tier.charAt(0) + tier.slice(1).toLowerCase();
}
